import { useState, type ReactElement } from "react";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { usePageTracking } from "@/hooks/use-track";
import { HelpTip } from "@/components/help-tip";
import { TutorialTip } from "@/components/TutorialTip";
import { usePageTour } from "@/components/ProductTour";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/empty-state";
import { TableSkeleton } from "@/components/loading-skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Upload, FileText, TrendingUp, Settings2, Trash2,
  RefreshCw, ChevronDown, ChevronUp, AlertCircle,
  CheckCircle2, Clock, XCircle, BarChart3, Plus,
  Image as ImageIcon, Activity, Target, Layers, Zap,
  ArrowUpRight, Shield,
} from "lucide-react";
import { COST_CATEGORIES } from "@shared/schema";
import type {
  UserQuoteImport,
  UserQuoteItemNormalized,
  UserPriceSignal,
  UserPricingRule,
} from "@shared/schema";

interface PricingAccuracyIndex {
  overallScore: number;
  matchStrength: "Excellent" | "Strong" | "Developing" | "Building" | "New";
  totalImports: number;
  totalItems: number;
  matchedItems: number;
  matchRate: number;
  uniqueSignals: number;
  avgSampleDepth: number;
  avgVariance: number;
  categoryBreakdown: Record<string, {
    signalCount: number;
    avgSamples: number;
    matchStrength: string;
  }>;
}

// ============================================================
// GAUGE COMPONENT
// ============================================================

function PAIGauge({ score, size = 220 }: { score: number; size?: number }) {
  const cx = size / 2;
  const cy = size / 2 + 10;
  const radius = size * 0.38;
  const strokeWidth = size * 0.09;
  const startAngle = 180;
  const endAngle = 0;
  const totalAngle = 180;

  const segments = [
    { from: 0, to: 20, color: "#ef4444" },
    { from: 20, to: 40, color: "#f97316" },
    { from: 40, to: 60, color: "#eab308" },
    { from: 60, to: 80, color: "#84cc16" },
    { from: 80, to: 100, color: "#22c55e" },
  ];

  const labels = [
    { value: 0, text: "0" },
    { value: 20, text: "20" },
    { value: 40, text: "40" },
    { value: 60, text: "60" },
    { value: 80, text: "80" },
    { value: 100, text: "100" },
  ];

  const polarToCartesian = (angle: number, r: number) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  };

  const describeArc = (startPct: number, endPct: number, r: number) => {
    const s = startAngle - (startPct / 100) * totalAngle;
    const e = startAngle - (endPct / 100) * totalAngle;
    const start = polarToCartesian(s, r);
    const end = polarToCartesian(e, r);
    const largeArc = Math.abs(s - e) > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  const clampedScore = Math.max(0, Math.min(100, score));
  const needleAngle = startAngle - (clampedScore / 100) * totalAngle;
  const needleLength = radius - strokeWidth / 2 - 4;
  const needleTip = polarToCartesian(needleAngle, needleLength);

  const needleBaseL = polarToCartesian(needleAngle + 90, 4);
  const needleBaseR = polarToCartesian(needleAngle - 90, 4);

  return (
    <svg viewBox={`0 0 ${size} ${size * 0.62}`} width={size} height={size * 0.62} data-testid="gauge-pai">
      {segments.map((seg, i) => (
        <path
          key={i}
          d={describeArc(seg.from, seg.to, radius)}
          fill="none"
          stroke={seg.color}
          strokeWidth={strokeWidth}
          strokeLinecap={i === 0 ? "round" : i === segments.length - 1 ? "round" : "butt"}
          opacity={0.85}
        />
      ))}

      {labels.map((label) => {
        const angle = startAngle - (label.value / 100) * totalAngle;
        const tickOuter = polarToCartesian(angle, radius + strokeWidth / 2 + 2);
        const tickInner = polarToCartesian(angle, radius + strokeWidth / 2 + 6);
        const textPos = polarToCartesian(angle, radius + strokeWidth / 2 + 16);
        return (
          <g key={label.value}>
            <line x1={tickOuter.x} y1={tickOuter.y} x2={tickInner.x} y2={tickInner.y} stroke="currentColor" strokeWidth={1.5} opacity={0.4} />
            <text x={textPos.x} y={textPos.y} textAnchor="middle" dominantBaseline="middle" fill="currentColor" fontSize={size * 0.045} opacity={0.5} fontWeight={500}>
              {label.text}
            </text>
          </g>
        );
      })}

      <polygon
        points={`${needleTip.x},${needleTip.y} ${needleBaseL.x},${needleBaseL.y} ${needleBaseR.x},${needleBaseR.y}`}
        fill="currentColor"
        opacity={0.8}
      />
      <circle cx={cx} cy={cy} r={6} fill="currentColor" opacity={0.7} />
      <circle cx={cx} cy={cy} r={3} fill="hsl(var(--background))" />
    </svg>
  );
}

// ============================================================
// PAI DASHBOARD
// ============================================================

function PAIDashboard() {
  const { data: pai, isLoading } = useQuery<PricingAccuracyIndex>({
    queryKey: ["/api/pricing-accuracy-index"],
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="h-48 animate-pulse bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!pai) return null;

  const getStrengthBadgeVariant = (strength: string): "default" | "secondary" | "outline" => {
    if (strength === "Excellent" || strength === "Strong") return "default";
    if (strength === "Developing") return "secondary";
    return "outline";
  };

  const getScoreGaugeColor = (score: number) => {
    if (score >= 80) return { bg: "bg-green-500", text: "text-white" };
    if (score >= 60) return { bg: "bg-lime-500", text: "text-white" };
    if (score >= 40) return { bg: "bg-yellow-500", text: "text-white" };
    if (score >= 20) return { bg: "bg-orange-500", text: "text-white" };
    return { bg: "bg-red-500", text: "text-white" };
  };

  const getStrengthGaugeColor = (strength: string) => {
    if (strength === "Excellent") return { bg: "bg-green-500", text: "text-white" };
    if (strength === "Strong") return { bg: "bg-lime-500", text: "text-white" };
    if (strength === "Developing") return { bg: "bg-yellow-500", text: "text-white" };
    if (strength === "Building") return { bg: "bg-orange-500", text: "text-white" };
    return { bg: "bg-red-500", text: "text-white" };
  };

  const badgeColors = getScoreGaugeColor(pai.overallScore);

  return (
    <div className="space-y-4">
      <Card data-testid="card-pai-dashboard">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex flex-col items-center shrink-0">
              <PAIGauge score={pai.overallScore} size={240} />
              <div className="flex flex-col items-center -mt-2">
                <span className="text-3xl font-bold" data-testid="text-pai-score">{pai.overallScore}</span>
                <span className={`mt-1 inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${badgeColors.bg} ${badgeColors.text}`} data-testid="badge-match-strength">
                  {pai.matchStrength}
                </span>
              </div>
            </div>

            <div className="flex-1 space-y-4 min-w-0">
              <div>
                <h3 className="text-lg font-semibold">Pricing Accuracy Index</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {pai.matchStrength === "New" && "Upload historical quotes to start building your pricing intelligence. The more data you provide, the more accurate your learned pricing becomes."}
                  {pai.matchStrength === "Building" && "Good start. Continue importing quotes to improve match accuracy. Aim for 5+ quotes across your common work categories."}
                  {pai.matchStrength === "Developing" && "Your pricing data is developing well. Adding more quotes in underrepresented categories will strengthen your pricing further."}
                  {pai.matchStrength === "Strong" && "Strong pricing intelligence. Your historical data provides reliable pricing guidance across most categories."}
                  {pai.matchStrength === "Excellent" && "Excellent pricing accuracy. Your imported data provides highly reliable, well-validated pricing across your work categories."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-md bg-muted/40">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Quotes Imported</span>
                  </div>
                  <span className="text-xl font-bold" data-testid="text-total-imports">{pai.totalImports}</span>
                </div>
                <div className="p-3 rounded-md bg-muted/40">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Match Rate</span>
                  </div>
                  <span className="text-xl font-bold" data-testid="text-match-rate">{pai.matchRate}%</span>
                </div>
                <div className="p-3 rounded-md bg-muted/40">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Price Signals</span>
                  </div>
                  <span className="text-xl font-bold" data-testid="text-unique-signals">{pai.uniqueSignals}</span>
                </div>
                <div className="p-3 rounded-md bg-muted/40">
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Avg. Depth</span>
                  </div>
                  <span className="text-xl font-bold" data-testid="text-avg-depth">{pai.avgSampleDepth}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {Object.keys(pai.categoryBreakdown).length > 0 && (
        <Card data-testid="card-category-intelligence">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Category Intelligence</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(pai.categoryBreakdown)
                .sort((a, b) => b[1].signalCount - a[1].signalCount)
                .map(([cat, data]) => (
                  <div key={cat} className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/30" data-testid={`category-intel-${cat}`}>
                    <span className="text-sm capitalize truncate">{cat.replace(/_/g, " ")}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">{data.signalCount} signals</span>
                      {(() => {
                        const c = getStrengthGaugeColor(data.matchStrength);
                        return (
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${c.bg} ${c.text}`}>
                            {data.matchStrength}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// IMPORT TAB
// ============================================================

function ImportTab() {
  const [uploadedFiles, setUploadedFiles] = useState<{ base64: string; filename: string }[]>([]);
  const [expandedImport, setExpandedImport] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: imports, isLoading } = useQuery<UserQuoteImport[]>({
    queryKey: ["/api/quote-imports"],
  });

  const pdfUploadMutation = useMutation({
    mutationFn: (data: { pdfBase64: string; filename?: string }) =>
      apiRequest("POST", "/api/quote-imports/pdf", data),
    onSuccess: async (response) => {
      const result = await response.json();
      queryClient.invalidateQueries({ queryKey: ["/api/quote-imports"] });
      queryClient.invalidateQueries({ queryKey: ["/api/price-signals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-accuracy-index"] });
      toast({
        title: "Quote imported",
        description: `Extracted and parsed ${result.itemCount} items${result.errors?.length > 0 ? ` with ${result.errors.length} warnings` : ""}`,
      });
    },
    onError: () => {
      toast({ title: "Failed to import quote", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/quote-imports/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quote-imports"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-accuracy-index"] });
      toast({ title: "Import deleted" });
    },
  });

  const recomputeMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/price-signals/recompute"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-signals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-accuracy-index"] });
      toast({ title: "Price signals recomputed" });
    },
    onError: () => {
      toast({ title: "Failed to recompute signals", variant: "destructive" });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      const isImage = file.type.startsWith("image/");
      const ext = file.name.toLowerCase().split(".").pop() || "";
      const isDocument = ["doc", "docx", "xls", "xlsx", "csv", "json", "txt"].includes(ext);
      if (!isPdf && !isImage && !isDocument) {
        toast({ title: "Unsupported file type", description: "Please upload PDF, images, Excel, Word, or text files", variant: "destructive" });
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: "File too large", description: "Max 10MB per file", variant: "destructive" });
        continue;
      }
      if (uploadedFiles.length >= 10) {
        toast({ title: "Maximum files reached", description: "You can upload up to 10 files at a time", variant: "destructive" });
        break;
      }

      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
      );
      setUploadedFiles(prev => [...prev, { base64, filename: file.name }]);
    }
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const textImportMutation = useMutation({
    mutationFn: (data: { sourceType: string; content: string; filename?: string }) =>
      apiRequest("POST", "/api/quote-imports", data),
    onSuccess: async (response) => {
      const result = await response.json();
      queryClient.invalidateQueries({ queryKey: ["/api/quote-imports"] });
      queryClient.invalidateQueries({ queryKey: ["/api/price-signals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-accuracy-index"] });
      toast({
        title: "Quote imported",
        description: `Parsed ${result.itemCount} items${result.errors?.length > 0 ? ` with ${result.errors.length} warnings` : ""}`,
      });
    },
    onError: () => {
      toast({ title: "Failed to import quote", variant: "destructive" });
    },
  });

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) return;

    for (const file of uploadedFiles) {
      const ext = file.filename.toLowerCase().split(".").pop() || "";
      const isTextBased = ["csv", "json", "txt"].includes(ext);

      try {
        if (isTextBased) {
          const text = atob(file.base64);
          const sourceType = ext === "csv" ? "csv" : ext === "json" ? "json" : "text";
          await textImportMutation.mutateAsync({
            sourceType,
            content: text,
            filename: file.filename,
          });
        } else {
          await pdfUploadMutation.mutateAsync({
            pdfBase64: file.base64,
            filename: file.filename,
          });
        }
      } catch {
        // error handled by mutation onError
      }
    }
    setUploadedFiles([]);
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case "completed": return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "processing": return <Clock className="h-4 w-4 text-yellow-500" />;
      case "failed": return <XCircle className="h-4 w-4 text-red-500" />;
      case "needs_review": return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Upload className="h-5 w-5 shrink-0" />
            Import Historical Quotes
          </CardTitle>
          <CardDescription>
            Upload past quotes to teach the system your pricing patterns. The more quotes you import, the stronger your Pricing Accuracy Index becomes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label
            className="border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
            data-testid="dropzone-quote-files"
          >
            <input
              type="file"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.json,.txt"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              data-testid="input-quote-files"
            />
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm font-medium" data-testid="text-dropzone-label">Click to upload or drag and drop</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, images, Excel, Word and more - up to 10MB each (max 10 files)</p>
          </label>

          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate flex-1" data-testid={`text-filename-${index}`}>{file.filename}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(index)}
                    data-testid={`button-remove-file-${index}`}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={handleUpload}
              disabled={pdfUploadMutation.isPending || textImportMutation.isPending || uploadedFiles.length === 0}
              data-testid="button-upload-quote"
            >
              {(pdfUploadMutation.isPending || textImportMutation.isPending) ? "Processing..." : `Import ${uploadedFiles.length > 0 ? uploadedFiles.length : ""} Quote${uploadedFiles.length !== 1 ? "s" : ""}`}
            </Button>
            <Button
              variant="outline"
              onClick={() => recomputeMutation.mutate()}
              disabled={recomputeMutation.isPending}
              data-testid="button-recompute-signals"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reanalyze
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <FileText className="h-5 w-5 shrink-0" />
            Import History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TableSkeleton />
          ) : !imports || imports.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No imports yet"
              description="Import your first historical quote above to start building your pricing intelligence."
            />
          ) : (
            <div className="space-y-2">
              {imports.map((imp) => (
                <ImportRow
                  key={imp.id}
                  imp={imp}
                  expanded={expandedImport === imp.id}
                  onToggle={() => setExpandedImport(expandedImport === imp.id ? null : imp.id)}
                  onDelete={() => deleteMutation.mutate(imp.id)}
                  getStatusIcon={getStatusIcon}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ImportRow({
  imp,
  expanded,
  onToggle,
  onDelete,
  getStatusIcon,
}: {
  imp: UserQuoteImport;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  getStatusIcon: (status: string | null) => ReactElement;
}) {
  const { data: items } = useQuery<UserQuoteItemNormalized[]>({
    queryKey: ["/api/quote-imports", imp.id, "items"],
    queryFn: () => fetch(`/api/quote-imports/${imp.id}/items`, { credentials: "include" }).then(r => r.json()),
    enabled: expanded,
  });

  const getMatchBadgeVariant = (matchType: string | null): "default" | "secondary" | "outline" => {
    if (matchType === "exact" || matchType === "ai_matched") return "default";
    if (matchType === "fuzzy") return "secondary";
    return "outline";
  };

  const getMatchLabel = (matchType: string | null) => {
    if (matchType === "exact") return "Exact";
    if (matchType === "ai_matched") return "AI Matched";
    if (matchType === "fuzzy") return "Fuzzy";
    return "Unmatched";
  };

  return (
    <div className="border rounded-md" data-testid={`import-row-${imp.id}`}>
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover-elevate"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 flex-wrap">
          {getStatusIcon(imp.parsingStatus)}
          <span className="font-medium text-sm">{imp.filename || `Import ${imp.id.slice(0, 8)}`}</span>
          <Badge variant="outline">{imp.sourceType?.toUpperCase()}</Badge>
          <span className="text-sm text-muted-foreground">{imp.itemCount} items</span>
          {imp.totalValue && (
            <span className="text-sm text-muted-foreground">
              Total: {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(parseFloat(imp.totalValue))}
            </span>
          )}
          {imp.parsingConfidence && (
            <Badge variant={parseFloat(imp.parsingConfidence) >= 70 ? "default" : "secondary"}>
              {imp.parsingConfidence}% matched
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`button-delete-import-${imp.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t p-3">
          {imp.errorMessage && (
            <div className="mb-3 p-2 bg-destructive/10 text-destructive rounded text-sm">
              {imp.errorMessage}
            </div>
          )}
          {items && items.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Raw Item</TableHead>
                  <TableHead>Matched To</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Unit Rate</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Match</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} data-testid={`normalized-item-${item.id}`}>
                    <TableCell className="font-medium text-sm">{item.rawItemName}</TableCell>
                    <TableCell className="text-sm">
                      {item.normalizedItemName || <span className="text-muted-foreground">Unmatched</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.category || "?"}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{item.quantity || "-"}</TableCell>
                    <TableCell className="text-sm">
                      {item.unitRate
                        ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(parseFloat(item.unitRate))
                        : "-"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.lineTotal
                        ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(parseFloat(item.lineTotal))
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getMatchBadgeVariant(item.matchType)}>
                        {getMatchLabel(item.matchType)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No items parsed</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// PRICE SIGNALS TAB (Enhanced)
// ============================================================

function SignalsTab() {
  const { data: signals, isLoading } = useQuery<UserPriceSignal[]>({
    queryKey: ["/api/price-signals"],
  });

  const recomputeMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/price-signals/recompute"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-signals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-accuracy-index"] });
    },
  });

  const fmt = (val: string | null | undefined) => {
    if (!val) return "-";
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(parseFloat(val));
  };

  const getConfidenceColor = (confidence: string | null) => {
    if (confidence === "high") return "text-green-600 dark:text-green-400";
    if (confidence === "medium") return "text-amber-600 dark:text-amber-400";
    return "text-muted-foreground";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <BarChart3 className="h-5 w-5 shrink-0" />
              Learned Price Signals
            </CardTitle>
            <CardDescription>
              Pricing data computed from your imported historical quotes. High and medium confidence signals are used when Learned Mode is active during quote generation.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending}
            data-testid="button-recompute-signals-tab"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Recompute
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <TableSkeleton />
        ) : !signals || signals.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title="No price signals yet"
            description="Import historical quotes first, then signals will be computed automatically. More quotes means stronger signals."
          />
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-xs text-muted-foreground">High confidence ({signals.filter(s => s.confidence === "high").length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-amber-500" />
                <span className="text-xs text-muted-foreground">Medium ({signals.filter(s => s.confidence === "medium").length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-muted-foreground" />
                <span className="text-xs text-muted-foreground">Low ({signals.filter(s => s.confidence === "low").length})</span>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Low Rate</TableHead>
                  <TableHead>Typical Rate</TableHead>
                  <TableHead>High Rate</TableHead>
                  <TableHead>Samples</TableHead>
                  <TableHead>Variance</TableHead>
                  <TableHead>Strength</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {signals
                  .sort((a, b) => {
                    const confOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
                    return (confOrder[a.confidence || "low"] || 2) - (confOrder[b.confidence || "low"] || 2);
                  })
                  .map((signal) => {
                    const cv = parseFloat(signal.varianceScore || "0");
                    const varianceLabel = cv < 0.1 ? "Very Low" : cv < 0.25 ? "Low" : cv < 0.4 ? "Moderate" : "High";
                    return (
                      <TableRow key={signal.id} data-testid={`signal-row-${signal.id}`}>
                        <TableCell className="font-medium text-sm">{signal.canonicalKey}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{(signal.category || "").replace(/_/g, " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{signal.unitType}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{fmt(signal.typicalUnitRateMin)}</TableCell>
                        <TableCell className={`text-sm font-medium ${getConfidenceColor(signal.confidence)}`}>
                          {fmt(signal.typicalUnitRateMedian)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{fmt(signal.typicalUnitRateMax)}</TableCell>
                        <TableCell className="text-sm">{signal.sampleCount}</TableCell>
                        <TableCell>
                          <Badge variant={cv < 0.25 ? "secondary" : "outline"} className="text-xs">
                            {varianceLabel}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={signal.confidence === "high" ? "default" : signal.confidence === "medium" ? "secondary" : "outline"}
                          >
                            {signal.confidence === "high" ? "Strong" : signal.confidence === "medium" ? "Moderate" : "Weak"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// PRICING RULES TAB
// ============================================================

const ruleFormSchema = z.object({
  name: z.string().min(1, "Rule name is required"),
  ruleType: z.enum(["multiplier", "fixed_rate", "min_price", "rounding"]),
  value: z.string().min(1, "Value is required"),
  categories: z.string().optional(),
  priority: z.string().optional(),
});

type RuleFormData = z.infer<typeof ruleFormSchema>;

function RulesTab() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const { data: rules, isLoading } = useQuery<UserPricingRule[]>({
    queryKey: ["/api/pricing-rules"],
  });

  const form = useForm<RuleFormData>({
    resolver: zodResolver(ruleFormSchema),
    defaultValues: {
      name: "",
      ruleType: "multiplier",
      value: "",
      categories: "",
      priority: "0",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/pricing-rules", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-rules"] });
      form.reset();
      setIsDialogOpen(false);
      toast({ title: "Pricing rule created" });
    },
    onError: () => {
      toast({ title: "Failed to create rule", variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiRequest("PATCH", `/api/pricing-rules/${id}`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-rules"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/pricing-rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pricing-rules"] });
      toast({ title: "Rule deleted" });
    },
  });

  const onSubmit = (data: RuleFormData) => {
    const numValue = parseFloat(data.value);
    if (isNaN(numValue)) {
      toast({ title: "Invalid value", variant: "destructive" });
      return;
    }

    const categories = data.categories
      ? data.categories.split(",").map(c => c.trim()).filter(Boolean)
      : undefined;

    const payload: any = {};
    switch (data.ruleType) {
      case "multiplier":
        payload.value = numValue;
        break;
      case "fixed_rate":
        payload.value = numValue;
        break;
      case "min_price":
        payload.minPrice = numValue;
        break;
      case "rounding":
        payload.roundTo = numValue;
        break;
    }

    createMutation.mutate({
      name: data.name,
      ruleType: data.ruleType,
      rulePayloadJson: payload,
      scopeFilterJson: categories ? { categories } : undefined,
      priority: parseInt(data.priority || "0"),
      enabled: true,
    });
  };

  const getRuleDescription = (rule: UserPricingRule) => {
    const payload = rule.rulePayloadJson as any;
    switch (rule.ruleType) {
      case "multiplier":
        return `Multiply by ${payload?.value || "?"}x`;
      case "fixed_rate":
        return `Fixed rate: ${new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(payload?.value || 0)}`;
      case "min_price":
        return `Min price: ${new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(payload?.minPrice || 0)}`;
      case "rounding":
        return `Round up to nearest ${new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(payload?.roundTo || 0)}`;
      default:
        return rule.ruleType;
    }
  };

  const getScopeDescription = (rule: UserPricingRule) => {
    const scope = rule.scopeFilterJson as any;
    if (!scope) return "All items";
    const parts: string[] = [];
    if (scope.categories?.length) parts.push(`Categories: ${scope.categories.join(", ")}`);
    if (scope.projectTypes?.length) parts.push(`Projects: ${scope.projectTypes.join(", ")}`);
    if (scope.regions?.length) parts.push(`Regions: ${scope.regions.join(", ")}`);
    return parts.length > 0 ? parts.join(" | ") : "All items";
  };

  const getValueLabel = (ruleType: string) => {
    switch (ruleType) {
      case "multiplier": return "Multiplier (e.g. 1.15 = +15%)";
      case "fixed_rate": return "Fixed rate per unit";
      case "min_price": return "Minimum price per item";
      case "rounding": return "Round up to nearest";
      default: return "Value";
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Settings2 className="h-5 w-5 shrink-0" />
              Pricing Rules
            </CardTitle>
            <CardDescription>
              Define custom rules to automatically adjust pricing during quote generation.
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-rule">
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Pricing Rule</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rule Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 15% London Uplift" {...field} data-testid="input-rule-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="ruleType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rule Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-rule-type">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="multiplier">Multiplier (scale prices)</SelectItem>
                            <SelectItem value="fixed_rate">Fixed Rate (override unit cost)</SelectItem>
                            <SelectItem value="min_price">Minimum Price (floor)</SelectItem>
                            <SelectItem value="rounding">Round Up</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{getValueLabel(form.watch("ruleType"))}</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} data-testid="input-rule-value" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="categories"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Apply To Categories (comma-separated, leave blank for all)</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. steel, demolition" {...field} data-testid="input-rule-categories" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority (higher = applied first)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} data-testid="input-rule-priority" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-rule">
                    {createMutation.isPending ? "Creating..." : "Create Rule"}
                  </Button>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <TableSkeleton />
        ) : !rules || rules.length === 0 ? (
          <EmptyState
            icon={Settings2}
            title="No pricing rules yet"
            description="Create rules to automatically adjust pricing based on categories, project types, or regions."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rule</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Effect</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Active</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                  <TableCell className="font-medium text-sm">{rule.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{rule.ruleType}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{getRuleDescription(rule)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{getScopeDescription(rule)}</TableCell>
                  <TableCell className="text-sm">{rule.priority || 0}</TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.enabled ?? true}
                      onCheckedChange={(checked) =>
                        toggleMutation.mutate({ id: rule.id, enabled: checked })
                      }
                      data-testid={`switch-rule-${rule.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => deleteMutation.mutate(rule.id)}
                      data-testid={`button-delete-rule-${rule.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// STRUCTURED DATA SCHEMAS
// ============================================================

const PRICING_INTELLIGENCE_WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Pricing Intelligence | The Proper Tool",
  "description": "Upload historical quotes to build your Pricing Accuracy Index. The Proper Tool learns your real-world rates and applies them automatically to future AI-generated quotes.",
  "url": "https://thepropertool.com/pricing-intelligence",
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  }
};

const PRICING_INTELLIGENCE_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the Pricing Accuracy Index (PAI)?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Pricing Accuracy Index (PAI) is a 0–100 score that measures how closely The Proper Tool's AI quote engine matches your own historical pricing. The more past quotes you import, the higher your PAI score and the more accurate your future auto-generated quotes become."
      }
    },
    {
      "@type": "Question",
      "name": "How do I improve my PAI score?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Import as many of your previous quotes as possible using the Imports tab. Upload PDFs, Excel spreadsheets, Word documents, or CSV files. Aim for at least 5 quotes across your most common work categories. The system extracts line-item rates, cross-references them against the built-in cost library, and raises your score as match confidence grows."
      }
    },
    {
      "@type": "Question",
      "name": "What file types can I import into Pricing Intelligence?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can import PDF quotes, images (JPG, PNG), Microsoft Excel (.xls, .xlsx), Microsoft Word (.doc, .docx), CSV files, and plain text files. Each file can be up to 10 MB and you can upload up to 10 files at once."
      }
    },
    {
      "@type": "Question",
      "name": "What are price signals?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Price signals are the per-category unit rates extracted from your imported quotes. For example, if several of your past quotes show groundworks at £45/m², that becomes a price signal. The Proper Tool uses these signals to calibrate its AI engine towards your actual market rates rather than generic national averages."
      }
    },
    {
      "@type": "Question",
      "name": "How does Pricing Intelligence affect my Auto Quotes?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Once Pricing Intelligence detects a strong enough signal for a trade category, it applies a learned rate adjustment to that category whenever the AI generates a new quote. This means the labour, materials, and plant costs in your quotes progressively align with what you actually charge, reducing manual editing and improving margin accuracy."
      }
    },
    {
      "@type": "Question",
      "name": "What is a Pricing Rule and how does it work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A Pricing Rule lets you set an explicit rate override or percentage adjustment for a specific trade category. Rules take precedence over learned price signals, giving you fine-grained control. For example, you can pin your bricklaying rate to £1.20 per brick regardless of what the AI infers from imported data."
      }
    }
  ]
};

// ============================================================
// MAIN PAGE
// ============================================================

export default function PricingIntelligencePage() {
  usePageTracking("Pricing Intelligence");
  usePageTour("pricing-intelligence");

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4 sm:space-y-6">
      <SEOHead
        title="Pricing Intelligence | The Proper Tool"
        description="Upload historical quotes to build your Pricing Accuracy Index. The Proper Tool learns your real-world rates and applies them automatically to future AI-generated quotes."
        canonical="/pricing-intelligence"
        ogType="website"
        schema={[PRICING_INTELLIGENCE_WEBPAGE_SCHEMA, PRICING_INTELLIGENCE_FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-page-title">
          Pricing Intelligence
          <HelpTip testId="button-help-pricing-intelligence" content="Import your past quotes and invoices to build a personalised pricing database. The more data you import, the higher your Pricing Accuracy Index (PAI) score, and the more accurate your future Auto Quotes become." />
        </h1>
        <p className="text-muted-foreground mt-1">
          Import historical quotes, build your pricing accuracy, and apply intelligent pricing to future quotes.
        </p>
      </div>

      <TutorialTip id="pricing-intelligence-intro" title="Teaching the Tool Your Pricing">
        Import your past quotes (PDFs or spreadsheets) using the <strong>Imports</strong> tab below to build a picture of how you price jobs. The <strong>Pricing Accuracy Index (PAI)</strong> gauge shows how closely our smart engine matches your historical rates. The higher the score, the less manual editing you need. Check <strong>Price Signals</strong> to see which trades have the biggest variance from your imported data.
      </TutorialTip>

      <PAIDashboard />

      <Tabs defaultValue="imports">
        <TabsList data-testid="tabs-pricing-intelligence">
          <TabsTrigger value="imports" data-testid="tab-imports">
            <Upload className="h-4 w-4 mr-2" />
            Imports
          </TabsTrigger>
          <TabsTrigger value="signals" data-testid="tab-signals">
            <TrendingUp className="h-4 w-4 mr-2" />
            Price Signals
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Settings2 className="h-4 w-4 mr-2" />
            Rules
          </TabsTrigger>
        </TabsList>

        <TabsContent value="imports" className="mt-4">
          <ImportTab />
        </TabsContent>

        <TabsContent value="signals" className="mt-4">
          <SignalsTab />
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <RulesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
